import type { AutocompleteAvatarProps } from './index.types';
import newConversationImage from 'Assets/icons/user-conversation.svg';
import { IconProps } from 'Components/shared/Icon/types';
import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '~/lib/ui/avatar';
import { cn } from '~/lib/utils';


/**
 * Avatar component for autocomplete options and values.
 *
 * This component handles avatar rendering for autocomplete components with:
 * - Regular contacts: Display avatar image or fallback initials
 * - Unsaved contacts: Display a user icon in muted background
 * - Consistent sizing and styling across different contexts
 *
 * @param props - AutocompleteAvatarProps containing the data and styling options
 * @returns A React element representing the avatar
 */
export const AutocompleteAvatar = ({
  data,
  size = 'md',
  className,
}: AutocompleteAvatarProps): React.ReactElement => {
  const isUnsavedContact = data?.type === 'unsaved-contact';

  const sizeConfig = {
    sm: {
      classes: 'w-6 h-6',
      textSize: 'text-[10px]',
      iconSize: 'tiny' as IconProps['size'],
    },
    md: {
      classes: 'w-8 h-8',
      textSize: 'text-xs',
      iconSize: 'small' as IconProps['size'],
    },
  };

  return (
    <Avatar className={cn(sizeConfig[size]?.classes, className)}>
      {isUnsavedContact ? (
        <AvatarFallback className={cn(sizeConfig[size]?.textSize, 'text-foreground')}>
          <img src={newConversationImage} alt="create conversation contact avatar" />
        </AvatarFallback>
      ) : (
        <>
          {data?.avatar && <AvatarImage src={data?.avatar} alt={data?.title} />}
          <AvatarFallback className={sizeConfig[size]?.textSize} name={data?.title} />
        </>
      )}
    </Avatar>
  );
};
